import {
  convertContentToRawText,
  decorator,
} from '../components/RichEditor/utils';
import { MIN_COLUMN_WIDTH } from '../components/Table';
import {
  defaultAccordionPanelConfiguration,
  defaultInfoCardConfiguration,
  defaultItemValues,
  defaultSlider,
  ItemTypes,
  MASKS_TYPE,
  uuid,
} from '../constants/constants';
import { boxPropsFromStyle } from '../shared/boxes/styleTemplate';
import { isFixedImageFit } from '../shared/boxes/imageFit';
import {
  calculateWidth,
  gridUnit,
  marginsObject,
  masksDefaultHeight,
  pageHeight,
  pageWidth,
} from '../constants/gridConfig';
import i18n from '../i18n';
import { computeColumnWidth } from './columns';
import { maxContainerHeight, mutateAddContainers } from './containers';
import { variableBoxName } from '../shared/variables/variableBoxNames';
import {
  classicTableCells,
  isImageValueMissing,
  isTableValueMissing,
  isTextBoxValueMissing,
} from '../shared/variables/variableBoxValues';
import { mutateCreateMask, mutateInjectMask } from './masks';
import { mutateResizeSummary, mutateTitlesIndexes } from './titles';
import { ContentState, EditorState } from 'draft-js';
import {
  mutateInsertVariableBox,
  mutateTakeOutItem,
  sortVariableOrderArray,
} from './variableOrderArray';
import { mutateRemoveTriggersWithTriggerId } from './triggers';
import { findWithRegex } from './dataSync';
import { getBoxesByColumnId, getColumnById, getContainerById } from '../store/lookups';
import { isPageBackgroundMask } from './pageBackground';
import { isTextValueMissing } from './address';
export const isATitleBox = (type) =>
  [
    ItemTypes.TITLE,
    ItemTypes.TITLE_2,
    ItemTypes.TITLE_3,
    ItemTypes.TITLE_4,
  ].includes(type);

/**
 * Returns true if the box is a linked title that should be hidden.
 * Linked titles are hidden when displayChapterSeparation is true (masks are shown).
 * When displayChapterSeparation is false (masks are hidden), linked titles are shown.
 */
export const isHiddenLinkedTitle = (box, configuration) =>
  !!box.linkedToMaskId && (configuration?.displayChapterSeparation ?? true) === true;

/**
 * Returns true if the box is a title injected from a chapter separation mask
 * (i.e. it lives in a column whose maskId points to a chapterSeparation mask).
 * These boxes should be excluded from sidebar / numbering when the linked title
 * is shown instead (displayChapterSeparation = false).
 */
export const isChapterSepMaskTitle = (box, columns, masks) => {
  if (!isATitleBox(box.type)) return false;
  const col = columns?.find((c) => c.id === box.columnId);
  if (!col?.maskId) return false;
  return !!(masks?.find((m) => m.id === col.maskId)?.chapterSeparation);
};

/**
 * Returns true if the box points to a column that no longer exists: it is
 * never rendered, so it must not appear in the summary nor take a number.
 * Boxes without columnId (mask boxes) are not concerned.
 */
export const isOrphanBox = (box, columns) =>
  !!box.columnId && Array.isArray(columns) && !columns.some((c) => c.id === box.columnId);

/**
 * Returns true if this title box should be excluded from sidebar/numbering.
 * Covers the hidden linked title, the hidden chapter sep mask title AND the
 * orphan title (see isOrphanBox).
 *
 * Chapter sep mask title boxes (all levels) are excluded only when displayChapterSeparation
 * is false — i.e. when the mask is hidden and the linked title takes its place.
 * When the mask is visible (displayChapterSeparation = true), its title boxes are
 * processed normally so they keep correct indexTitles.
 */
export const isExcludedChapterTitle = (box, columns, masks, configuration) =>
  isOrphanBox(box, columns) ||
  isHiddenLinkedTitle(box, configuration) ||
  (isChapterSepMaskTitle(box, columns, masks) && !(configuration?.displayChapterSeparation ?? true));

export const isAOneRowBox = (type) =>
  [
    ItemTypes.TITLE,
    ItemTypes.TITLE_2,
    ItemTypes.TITLE_3,
    ItemTypes.TITLE_4,
    ItemTypes.SUMMARY,
    ItemTypes.TABLE,
    ItemTypes.MASK,
    ItemTypes.DRAW,
    ItemTypes.SECTION,
    ItemTypes.SECTION_VARIABLE,
    ItemTypes.LINE_BREAK,
    ItemTypes.FILE_PDF,
    ItemTypes.FILE_PDF_VARIABLE,
    ItemTypes.SPACE,
    ItemTypes.RECURSIVE_MODEL,
  ].includes(type);

// Une box pleine largeur (isAOneRowBox ci-dessus : titre, tableau, sommaire...)
// ne se dépose que dans une colonne seule, et le canvas refuse d'ajouter une
// colonne à côté (BorderColumn, hasAOneRowBox). Sert à masquer les réglages qui
// permettraient de contourner cette règle après coup (ex. changer la
// disposition du container depuis le panel de droite). Descend dans les lignes
// imbriquées des colonnes.
export const containerHasOneRowBox = (container, columns, rowContainers, boxes) => {
  const hasOneRowBox = (columnId) =>
    getBoxesByColumnId(boxes, columnId).some((b) => isAOneRowBox(b.type));
  return (container?.columnsIds ?? []).some((cId) => {
    if (hasOneRowBox(cId)) return true;
    const rowContainerIds = getColumnById(columns, cId)?.rowContainerIds ?? [];
    return rowContainerIds.some((rowId) =>
      (getContainerById(rowContainers, rowId)?.columnsIds ?? []).some(hasOneRowBox)
    );
  });
};

export const isADrawBox = (type) =>
  [
    ItemTypes.MASK,
    ItemTypes.DRAW,
    ItemTypes.SECTION,
    ItemTypes.SECTION_VARIABLE,
    ItemTypes.FILE_PDF,
    ItemTypes.FILE_PDF_VARIABLE,
    ItemTypes.RECURSIVE_MODEL,
  ].includes(type);

export const isADefaultBox = (type) => [ItemTypes.LINE_BREAK].includes(type);

export const isAVariableBox = (type) =>
  [
    ItemTypes.CHECKBOXS_VARIABLE,
    ItemTypes.SELECTOR_VARIABLE,
    ItemTypes.TEXT_VARIABLE,
    ItemTypes.SECTION_VARIABLE,
    ItemTypes.IMG_VARIABLE,
    ItemTypes.TABLE_VARIABLE,
    ItemTypes.QUESTION,
    ItemTypes.FILE_PDF_VARIABLE,
  ].includes(type);

// Box variable dont la valeur se choisit parmi une liste de réponses.
export const isAnswersVariableBox = (type) =>
  [
    ItemTypes.CHECKBOXS_VARIABLE,
    ItemTypes.SELECTOR_VARIABLE,
    ItemTypes.QUESTION,
  ].includes(type);

// Où lire la liste des réponses et le drapeau de sélection d'une de ces box,
// comme le fait le formulaire plein écran (FormCheckboxInfos / FormSelector).
export const selectionKeysForBox = (box) =>
  box?.type === ItemTypes.SELECTOR_VARIABLE
    ? { arrayKey: 'options', selectionKey: 'selected' }
    : { arrayKey: 'checkboxs', selectionKey: 'checked' };

export const isASelectionBox = (type) =>
  [
    ItemTypes.MASK,
    ItemTypes.SECTION,
    ItemTypes.RECURSIVE_SECTION,
    ItemTypes.RECURSIVE_MODEL,
    ItemTypes.SECTION_VARIABLE,
    ItemTypes.FILE_PDF,
    ItemTypes.SECTION_LINK,
    ItemTypes.FILE_PDF_VARIABLE,
  ].includes(type);

export const isAWritableBox = (type) =>
  [
    ItemTypes.TEXT,
    ItemTypes.TEXT_VARIABLE,
    ItemTypes.TABLE,
    ItemTypes.TITLE,
    ItemTypes.TITLE_2,
    ItemTypes.TITLE_3,
    ItemTypes.TITLE_4,
  ].includes(type);

export const isATextBox = (type) =>
  [ItemTypes.TEXT, ItemTypes.TEXT_VARIABLE].includes(type);
export const isATableBox = (type) =>
  [ItemTypes.TABLE, ItemTypes.TABLE_VARIABLE].includes(type);
export const isAImgBox = (type) =>
  [ItemTypes.IMG, ItemTypes.IMG_VARIABLE].includes(type);

/** Registre des box à plusieurs textes riches — cf. shared/boxes/multiTextBoxes. */
export { MULTI_TEXT_CONTENT_KEYS, isAMultiTextBox } from '../shared/boxes/multiTextBoxes';

export const boxBounding = (box) => {
  const width = boxBoundingWidth(box);
  const height = boxBoundingHeight(box);
  return {
    width,
    height,
    top: box.clientTop,
    left: box.clientLeft,
    bottom: box.clientTop + height,
    right: box.clientLeft + width,
    vertical: box.clientTop + height / 2,
    horizontal: box.clientLeft + width / 2,
  };
};

export const boxBoundingHeight = (box) => {
  if (!box.clientHeight) {
    console.error(
      'boxBoundingHeight',
      `box.clientHeight  for box ${box.id} is undefined`
    );
  }
  // bounding Rect heigth is always vertical
  return box.clientHeight;
  // if (box.rotation && ((box.rotation > 90 && box.rotation < 180) || (box.rotation > 270 && box.rotation < 360))) {
  //   if (!box.clientWidth) { console.error('boxBoundingHeight', `box.clientWidth  for box ${box.id} is undefined`);}
  //   return box.clientWidth;
  // } else {
  //   return box.clientHeight;
  // }
};
export const boxHeight = (box) => {
  let h = boxContentHeight(box);
  if (!h) {
    console.error('boxHeight', 'boxHeight can not be computed');
  }
  return h;
};
export const boxContentHeight = (box) => {
  switch (box.type) {
    case ItemTypes.IMG:
    case ItemTypes.IMG_VARIABLE:
      // Contenir / remplir : la box a sa propre hauteur, l'image s'y ajuste.
      if (isFixedImageFit(box) && typeof box.height === 'number') {
        return box.height;
      }
      if (box.content.ratio) {
        return box.width / box.content.ratio - 3;
      } else {
        return box.width - 15; // default image => ratio 1
      }
    default:
      if (box.rotation && box.rotation > 0) {
        if (!box.clientHeight || !box.clientWidth || !box.width) {
          console.log(
            'boxContentHeight',
            `box.clientHeight or box.clientWidth or box.width for box ${box.id} is undefined`
          );
        }
        return (
          (boxBoundingWidth(box) -
            Math.abs(box.width * Math.cos((box.rotation * Math.PI) / 180))) /
          Math.abs(Math.sin((box.rotation * Math.PI) / 180))
        );
      } else {
        return box.clientHeight;
      }
  }
};
export const boxBoundingWidth = (box) => {
  if (!box?.clientWidth) {
    console.error(
      'boxBoundingWidth',
      `box.clientWidth  for box ${box.id} is undefined`
    );
  }
  return box.clientWidth;
  // if (box.rotation && ((box.rotation > 90 && box.rotation < 180) || (box.rotation > 270 && box.rotation < 360))) {
  //   if (!box.clientHeight) { console.error('boxBoundingWidth', `box.clientHeight  for box ${box.id} is undefined`);}
  //   return box.clientHeight;
  // } else {
  //   return box.clientWidth;
  // }
};
export const boxWidth = (box) => {
  let w = boxContentWidth(box);
  if (!w) {
    console.error('boxWidth', 'boxWidth can not be computed');
  }
  return w;
};
export const boxContentWidth = (box) => {
  let totalWidth;
  if (isATableBox(box.type)) {
    totalWidth = box.columns.reduce((acc, col) => acc + col.width, 0);
  } else {
    totalWidth = box.width;
  }
  if (!totalWidth) {
    console.error('boxContentWidth', 'box.width is not defined');
  }
  return totalWidth;
};

export const boxVerticalSize = (box) => {
  if (
    box.rotation &&
    ((box.rotation > 90 && box.rotation < 180) ||
      (box.rotation > 270 && box.rotation < 360))
  ) {
    return boxWidth(box);
  } else {
    return boxHeight(box);
  }
};
export const boxHorizontalSize = (box) => {
  let hs;
  if (
    box.rotation &&
    ((box.rotation > 90 && box.rotation < 180) ||
      (box.rotation > 270 && box.rotation < 360))
  ) {
    hs = boxHeight(box);
  } else {
    hs = boxWidth(box);
  }
  return hs;
};
export const boxBoundingTop = (box) => {
  if (!box.clientTop) {
    console.error(
      'boxBoundingTop',
      `box.clientTop  for box ${box.id} is undefined`
    );
  }
  return box.clientTop;
};

export const boxBoundingLeft = (box) => {
  if (!box.clientLeft) {
    console.error(
      'boxBoundingLeft',
      `box.clientLeft  for box ${box.id} is undefined`
    );
  }
  return box.clientLeft;
};

export const bBoxMove = ({ side, box, targetValue }) => {
  const bBoxMove = {
    top: 0,
    left: 0,
  };
  const bBox = boxBounding(box);
  if (side === 'top') {
    bBoxMove.top = targetValue - bBox.top;
  } else if (side === 'bottom') {
    bBoxMove.top = targetValue - bBox.bottom;
  } else if (side === 'left') {
    bBoxMove.left = targetValue - bBox.left;
  } else if (side === 'right') {
    bBoxMove.left = targetValue - bBox.right;
  } else if (side === 'vertical') {
    bBoxMove.top = targetValue - bBox.vertical;
  } else if (side === 'horizontal') {
    bBoxMove.left = targetValue - bBox.horizontal;
  } else if (side === 'vertical-center') {
    bBoxMove.top = targetValue - bBox.vertical;
  } else if (side === 'horizontal-center') {
    bBoxMove.left = targetValue - bBox.horizontal;
  }
  return bBoxMove;
};

export const boxAffectedSide = (alignmentSide) => {
  if (
    alignmentSide === 'right' ||
    alignmentSide === 'horizontal' ||
    alignmentSide === 'horizontal-center'
  ) {
    return 'left';
  } else if (
    alignmentSide === 'bottom' ||
    alignmentSide === 'vertical' ||
    alignmentSide === 'vertical-center'
  ) {
    return 'top';
  } else {
    return alignmentSide;
  }
};

// La box sélectionnée se place librement (mode dessin) : avancer/reculer,
// verrou de position et rotation s'appliquent à elle.
export const isSelectedBoxFreelyPlaced = ({
  selectedBoxId,
  boxes,
  columns,
  masks,
  maskEditing,
}) => {
  if (!selectedBoxId) return false;
  const selectedBox = boxes.find((b) => b.id === selectedBoxId);
  // Box d'un masque en cours d'édition (en-tête, pied de page, décor de page,
  // page de garde...) : un masque est toujours en mode dessin.
  if (!selectedBox) {
    return maskEditing.some((maskId) =>
      masks
        .find((m) => m.id === maskId)
        ?.boxes?.some((b) => b.id === selectedBoxId)
    );
  }
  // Les boxes en attente d'un contenu (section, section variable, PDF,
  // masque) sont rendues directement par SelectionBox, sans Dragger ni
  // Resizer : elles occupent leur colonne, ne se déplacent pas et ne se
  // superposent à rien. Avancer/reculer, verrou et rotation n'y produisaient
  // donc aucun effet, tout en occupant le panel.
  if (isASelectionBox(selectedBox.type)) return false;
  return !!getColumnById(columns, selectedBox.columnId)?.drawMode;
};

export const getAlignedBoxTargetValue = ({ draft, selectedBox, side }) => {
  const column = draft.columns.find((c) => c.id === selectedBox.columnId);
  const mask = draft.masks.find((m) => m.id === selectedBox.maskId);
  // Le décor de page couvre toute la page : ses box s'alignent sur les bords de
  // la page, sans les marges.
  const backgroundHeight =
    isPageBackgroundMask(mask)
      ? (draft.landscape ? pageWidth : pageHeight)
      : null;
  const hasVerticalMargin =
    (column &&
      (draft.landscape ||
        (column.drawMode && (column.isOldPage || column.maskId)))) ||
    (mask &&
      mask.id !== MASKS_TYPE.HEADER.id &&
      mask.id !== MASKS_TYPE.FOOTER.id &&
      !isPageBackgroundMask(mask));
  const margins = marginsObject(draft.configuration, hasVerticalMargin);
  let targetValue;

  if (side === 'top') {
    targetValue = hasVerticalMargin ? margins[side] : 0;
  } else if (side === 'left') {
    targetValue = margins[side];
  } else if (side === 'right') {
    targetValue = draft.landscape
      ? pageHeight - margins.right
      : pageWidth - margins.right;
  } else if (side === 'bottom') {
    if (backgroundHeight !== null) {
      targetValue = backgroundHeight;
    } else if (hasVerticalMargin) {
      targetValue = draft.landscape
        ? pageWidth - margins.bottom
        : pageHeight - margins.bottom;
    } else if (column) {
      // Peut être une sous-colonne de ligne imbriquée (cf. rowContainers).
      const containerHeight = (
        draft.containers.find((c) => c.columnsIds.includes(column.id)) ??
        draft.rowContainers?.find((c) => c.columnsIds.includes(column.id))
      )?.height;
      targetValue = containerHeight;
    } else if (mask.id === MASKS_TYPE.FOOTER.id) {
      targetValue = mask.height ?? masksDefaultHeight.footer;
    } else {
      targetValue = mask.height ?? masksDefaultHeight.header;
    }
  } else if (side === 'horizontal-center') {
    const pageUsableLeft = margins.left;
    const pageUsableRight = (draft.landscape ? pageHeight : pageWidth) - margins.right;
    targetValue = (pageUsableLeft + pageUsableRight) / 2;
  } else if (side === 'vertical-center') {
    const isDrawModeWithoutMask = column?.drawMode && !mask;
    const containerHeight = column
      ? draft.containers.find((c) => c.columnsIds.includes(column.id))?.height
      : null;
    const pageUsableTop =
      isDrawModeWithoutMask || !hasVerticalMargin ? 0 : margins.top;
    const fullPageHeight = draft.landscape ? pageWidth : pageHeight;
    let pageUsableBottom;
    if (isDrawModeWithoutMask) {
      pageUsableBottom = containerHeight ?? fullPageHeight;
    } else if (backgroundHeight !== null) {
      pageUsableBottom = backgroundHeight;
    } else if (!hasVerticalMargin) {
      pageUsableBottom = fullPageHeight;
    } else {
      pageUsableBottom = fullPageHeight - margins.bottom;
    }
    targetValue = (pageUsableTop + pageUsableBottom) / 2;
  }
  return targetValue;
};

export const alignedBoxValues = ({ side, box, targetValue }) => {
  const bBoxMoveValues = bBoxMove({ side, box, targetValue });
  const boxPropertyside = boxAffectedSide(side);
  const moveValue = bBoxMoveValues[boxPropertyside]; // + delta;

  return {
    propertyToAlign: boxPropertyside,
    value: moveValue,
  };
};

export const rotatedBoxDelta = ({ rotation, direction, x, y }) => {
  // Direction corresponds to the handler selected, could be "right" or "bottom"
  let width, height;
  if (rotation === 0 || !rotation) {
    width = direction === 'right' ? x : 0;
    height = direction === 'bottom' ? y : 0;
  } else {
    width =
      direction === 'right'
        ? x * Math.cos((rotation * Math.PI) / 180) +
          y * Math.sin((rotation * Math.PI) / 180)
        : 0;
    height =
      direction === 'bottom'
        ? y * Math.cos((rotation * Math.PI) / 180) -
          x * Math.sin((rotation * Math.PI) / 180)
        : 0;
  }
  return {
    width,
    height,
  };
};

export const getRatioMove = ({ width, height }, ratio) => {
  // ratio = width / height
  if (!ratio) return { width, height };
  return {
    width,
    height: width / ratio,
  };
};

export const getBoxBaseCornerRealPosition = (box, initialHeight) => {
  const { left: boxLeft, top: boxTop, width } = box;
  const realHeight = initialHeight ?? boxHeight(box);

  const boxCenterLeft = boxLeft + width / 2;
  const boxCenterTop = boxTop + realHeight / 2;
  const radius = Math.sqrt(
    Math.pow(width / 2, 2) + Math.pow(realHeight / 2, 2)
  );
  const cornerAngle = (Math.atan(realHeight / width) * 180) / Math.PI;
  const rotatedBoxBaseCornerLeft =
    boxCenterLeft -
    radius * Math.cos(((box.rotation + cornerAngle) * Math.PI) / 180);
  const rotatedBoxBaseCornerTop =
    boxCenterTop -
    radius * Math.sin(((box.rotation + cornerAngle) * Math.PI) / 180);
  return {
    top: rotatedBoxBaseCornerTop,
    left: rotatedBoxBaseCornerLeft,
  };
};

export const getRotatedBoxPositionAfterResize = (
  box,
  newWidth,
  newHeight,
  initialHeight
) => {
  const rotatedBoxBaseCornerPosition = getBoxBaseCornerRealPosition(
    box,
    initialHeight
  );
  const newRadius = Math.sqrt(
    Math.pow(newWidth / 2, 2) + Math.pow(newHeight / 2, 2)
  );
  const newCornerAngle = (Math.atan(newHeight / newWidth) * 180) / Math.PI;
  const newBoxCenterLeft =
    rotatedBoxBaseCornerPosition.left +
    newRadius * Math.cos(((box.rotation + newCornerAngle) * Math.PI) / 180);
  const newBoxCenterTop =
    rotatedBoxBaseCornerPosition.top +
    newRadius * Math.sin(((box.rotation + newCornerAngle) * Math.PI) / 180);
  return {
    top: newBoxCenterTop - newHeight / 2,
    left: newBoxCenterLeft - newWidth / 2,
  };
};

// export const isInPage = (box, pageId) => box.pageId === pageId;

export const isAfterFrom = (box, fromValue) =>
  box.top + boxBoundingHeight(box) > fromValue;
export const isBeforeFrom = (box, fromValue) => box.top < fromValue;
export const isInMask = (box) => !!box.maskId;

// `addCopy` attend { sourceId, item } : l'id de l'élément d'origine sert
// uniquement à dédoublonner le presse-papiers, il n'est pas recopié dans la box
// créée au collage.
export const CopyBox = (box, addCopy) => {
  const newBox = {
    ...box,
  };
  delete newBox.left;
  delete newBox.top;
  delete newBox.id;
  delete newBox.maskId;
  delete newBox.idForMask;
  addCopy({ sourceId: box.id, item: newBox });
};

// Copie d'un container : sa grille ET son contenu. Une colonne peut empiler des
// lignes (rowContainers), qui portent elles-mêmes des sous-colonnes, elles aussi
// divisibles. Le presse-papiers décrit donc cet arbre (`layout`) et situe chaque
// box par le chemin de sa colonne (`columnPath` : [idxColonne] au premier
// niveau, puis [..., idxLigne, idxSousColonne]). Auparavant seules les box
// posées directement dans les colonnes du container étaient prises : un
// container à plusieurs lignes se collait vide, sans sa grille interne.
export const CopyContainer = ({
  addCopy,
  boxes,
  columns,
  rowContainers = [],
  selectedContainer,
}) => {
  const columnById = new Map(columns.map((column) => [column.id, column]));
  const rowById = new Map((rowContainers ?? []).map((row) => [row.id, row]));
  const containerColumnIds = selectedContainer?.columnsIds ?? [];
  const containerColumns = containerColumnIds.map((id) => columnById.get(id));
  const drawMode = containerColumns.some((column) => column?.drawMode);
  const columnMaskId = containerColumns.find((column) => column?.maskId)?.maskId;

  const collectedBoxes = [];

  const describeColumn = (columnId, columnPath) => {
    const column = columnById.get(columnId);
    const rowIds = column?.rowContainerIds ?? [];

    if (!rowIds.length) {
      boxes
        .filter((box) => box.columnId === columnId)
        .forEach((box) => collectedBoxes.push({ box, columnPath }));
      // verticalAlign : réglage de mise en page de la cellule (répartition de
      // ses box), sans quoi le collage reviendrait au centrage par défaut.
      return { size: column?.size ?? 1, verticalAlign: column?.verticalAlign };
    }

    return {
      size: column?.size ?? 1,
      verticalAlign: column?.verticalAlign,
      rows: rowIds.map((rowId, rowIdx) => ({
        columns: (rowById.get(rowId)?.columnsIds ?? []).map(
          (subColumnId, subIdx) =>
            describeColumn(subColumnId, [...columnPath, rowIdx, subIdx])
        ),
      })),
    };
  };

  const layout = containerColumnIds.map((columnId, idx) =>
    describeColumn(columnId, [idx])
  );
  const hasRows = layout.some((column) => column.rows?.length);

  // Raccourci box seule : garder le container n'a de sens que s'il y a une
  // grille à préserver. Il laisse tomber le maskId, d'où la garde.
  if (!drawMode && !hasRows && collectedBoxes.length === 1 && !columnMaskId) {
    CopyBox(collectedBoxes[0].box, addCopy);
    return;
  }

  const newBoxes = collectedBoxes.map(({ box, columnPath }) => {
    const newBox = {
      ...box,
      oldId: box.id,
      oldBoxIds: box.boxIds,
      columnPath,
    };
    delete newBox.id;
    delete newBox.idForMask;
    delete newBox.columnId;
    delete newBox.maskId;

    return newBox;
  });

  addCopy({
    sourceId: selectedContainer.id,
    item: {
      type: ItemTypes.CONTAINER_COPIED,
      boxes: newBoxes,
      // `columnsDisposition` (tailles du premier niveau) reste pour les
      // consommateurs qui ne lisent que la largeur des colonnes.
      columnsDisposition: layout.map(({ size }) => ({ size })),
      layout,
      drawMode,
      ...(columnMaskId && { maskId: columnMaskId }),
      // Espacements propres au container : réglages de mise en page, ils
      // suivent la copie comme la grille.
      copiedContainer: {
        height: selectedContainer.height,
        lineSpacing: selectedContainer.lineSpacing,
        columnGap: selectedContainer.columnGap,
      },
    },
  });
};

export const prepareBoxesForContainerPaste = ({
  boxes,
  configuration,
  titles,
}) => {
  const newBoxes = boxes.map((b) =>
    initializeBoxContent({
      box: b,
      configuration,
      titles,
    })
  );

  newBoxes.forEach((b, idx) => {
    if (b.type === ItemTypes.GROUP_BOXES && b.oldBoxIds) {
      newBoxes[idx].boxIds = newBoxes
        .filter((bAdd) => b.oldBoxIds.includes(bAdd.oldId))
        .map(({ id }) => id);
    }
  });

  newBoxes.forEach((b) => {
    delete b.oldId;
    delete b.oldBoxIds;
  });

  return newBoxes;
};

export const orderBoxesByContainer = ({ containers, boxesToSort }) => {
  const columnsIdOrdered = containers.map((cont) => cont.columnsIds).flat();
  boxesToSort.sort((boxA, boxB) => {
    if (boxA.columnId !== boxB.columnId && columnsIdOrdered) {
      return (
        columnsIdOrdered.indexOf(boxA.columnId) -
        columnsIdOrdered.indexOf(boxB.columnId)
      );
    }
    const boxAHasText = isATitleBox(boxA.type) || isATextBox(boxA.type);
    const boxBHasText = isATitleBox(boxB.type) || isATextBox(boxB.type);
    const boxAHasGroup = boxA.type === ItemTypes.GROUP_BOXES;
    const boxBHasGroup = boxB.type === ItemTypes.GROUP_BOXES;

    if (boxAHasGroup && !boxBHasGroup) {
      return 1; // A après B
    } else if (!boxAHasGroup && boxBHasGroup) {
      return -1;
    } else if (boxAHasText && !boxBHasText) {
      return 1; // A après B
    } else if (!boxAHasText && boxBHasText) {
      return -1; // A avant B
    } else {
      return (boxA.groupTop ?? boxA.top) - (boxB.groupTop ?? boxB.top); // A avant B si negatif
    }
  });
  return boxesToSort;
};

export const mutateVariablesBoxes = (draft, maskId = null, newBoxes) => {
  const variablesBoxesNames = draft.boxes
    .filter(
      (box) =>
        isAVariableBox(box.type) && newBoxes.every((b) => b.id !== box.id)
    )
    .map((b) => convertContentToRawText(b.name));
  const newBoxesNames = [];
  orderBoxesByContainer({
    containers: draft.containers,
    boxesToSort: newBoxes,
  });
  return newBoxes.map((box) => {
    if (maskId || !isAVariableBox(box.type)) return box;

    const regex = new RegExp(/#{.*?}/, 'm');
    let baseNameBox;
    if (box.name) {
      baseNameBox =
        typeof box.name === 'string'
          ? box.name
          : convertContentToRawText(box.name);
    } else if (
      box.type === ItemTypes.IMG_VARIABLE &&
      findWithRegex(regex, box.content.src)
    ) {
      baseNameBox = i18n.t(box.content.src?.slice(2, -1));
    } else {
      baseNameBox = i18n.t(box.type);
    }

    const oldNames = variablesBoxesNames.concat(newBoxesNames);
    const newName = variableBoxName({
      baseName: baseNameBox,
      typeLabel: i18n.t(box.type),
      takenNames: oldNames,
    });

    newBoxesNames.push(newName);
    mutateInsertVariableBox(draft, box);
    return {
      ...box,
      name: EditorState.createWithContent(
        ContentState.createFromText(newName),
        decorator
      ),
    };
  });
};

export const mutateBoxesOrderInfinite = (draft, maskId = null) => {
  const boxesToSort =
    maskId === null
      ? draft.boxes
      : draft.masks.find((m) => m.id === maskId).boxes;
  orderBoxesByContainer({ containers: draft.containers, boxesToSort });
};

export const mutateAddBoxes = ({ draft, newBoxes = [], maskId = null }) => {
  newBoxes = mutateVariablesBoxes(draft, maskId, newBoxes);
  if (maskId === null) {
    draft.boxes.push(newBoxes);
    draft.boxes = draft.boxes.flat();
  } else {
    const mask = draft.masks.find((m) => m.id === maskId);
    let newMasksBoxes = newBoxes;
    if (maskId === MASKS_TYPE.HEADER.id || maskId === MASKS_TYPE.FOOTER.id) {
      const mask = draft.masks.find((m) => m.id === maskId);
      const newWidth =
        (maskId === MASKS_TYPE.HEADER.id
          ? mask.height ?? masksDefaultHeight.header
          : mask.height ?? masksDefaultHeight.footer) *
        (2 / 3);
      newMasksBoxes = newMasksBoxes.map((b) => {
        return isAImgBox(b.type) && !b.copied ? { ...b, width: newWidth } : b;
      });
    }
    mask.boxes.push(newMasksBoxes);
    mask.boxes = mask.boxes.flat();
  }
  mutateBoxesOrderInfinite(draft, maskId);
  if (newBoxes.some((box) => isATitleBox(box.type))) {
    mutateTitlesIndexes(draft, maskId);
    mutateResizeSummary(draft, maskId);
  }
};

export const sortBoxesForMove = (boxes, moveY) => {
  if (moveY > 0) {
    return sortBoxesToGoDown(boxes);
  }
  return sortBoxesToGoUp(boxes);
};

export const sortBoxesToGoDown = (boxes) => {
  return boxes.sort((boxA, boxB) => {
    const boxAHeight = boxBoundingHeight(boxA);
    const boxBHeight = boxBoundingHeight(boxB);
    return boxA.top + boxAHeight - (boxB.top + boxBHeight);
  });
};

export const sortBoxesToGoUp = (boxes) => {
  return boxes.sort((boxA, boxB) => boxB.top - boxA.top);
};

export const boxesToMoveInfinite = (boxes, fromValue) => {
  return boxes.filter((box) => {
    return box.top >= fromValue;
  });
};

export const mutateMoveBoxesBatchInfinite = (
  draft,
  moveObject,
  except = []
) => {
  const { to, from } =
    moveObject?.to && moveObject.from ? moveObject : draft.slider;
  const moveY = to.value - from.value;
  let boxesToMove = boxesToMoveInfinite(draft.boxes, from.value);
  boxesToMove.forEach((box) => {
    box.top += moveY;
  });
  draft.slider = defaultSlider;
};

export const mutateMoveBoxInfinite = (draft, left, top, boxId) => {
  const boxes = [...draft.boxes, ...draft.masks.map((m) => m.boxes).flat()];
  const box = boxes.find((box) => box.id === boxId);
  if (box?.lockedPosition) {
    return;
  }
  if (box.type === ItemTypes.GROUP_BOXES) {
    mutateMoveBoxFromGroup({
      box,
      boxes,
      newLeft: left,
      newTop: top,
      oldLeft: box.left,
      oldTop: box.top,
    });
  }
  box.top = top;
  box.left = left;
  mutateBoxesOrderInfinite(draft);
  if (isATitleBox(box.type)) {
    mutateTitlesIndexes(draft);
    mutateResizeSummary(draft);
  }
  // Réordonner variableOrderArray si c'est une box variable
  if (isAVariableBox(box.type)) {
    sortVariableOrderArray(draft);
  }
};

export const mutateMoveBoxFromGroup = ({
  box,
  boxes,
  newLeft,
  newTop,
  oldLeft,
  oldTop,
}) => {
  const boxesToMove = boxes.filter(({ id }) => box.boxIds.includes(id));
  boxesToMove.forEach((b) => {
    b.left += newLeft - oldLeft;
    b.top += newTop - oldTop;
    b.groupTop = newTop;
    b.originalLeft += newLeft - oldLeft;
    b.originalTop += newTop - oldTop;
  });
};

/**
 * Cadre d'un groupe : au niveau de son membre le plus haut, qu'il recouvre
 * pour être saisi (à zIndex égal, un groupe est rendu après les autres box de
 * sa colonne, cf. orderBoxesByContainer). Le zIndex propre du groupe n'y entre
 * pas : il ne déplaçait que ce cadre transparent, jamais les membres.
 */
export const groupFrameZIndex = (boxesInGroup) =>
  boxesInGroup.length ? Math.max(...boxesInGroup.map((b) => ~~b.zIndex)) : 0;

/**
 * Avancer / reculer d'un plan (panel de droite, mode dessin) : la box passe
 * au-dessus (ou au-dessous) de la plus proche des autres box de sa colonne.
 * Rend les nouveaux zIndex `[{ id, zIndex }]`, vide quand rien ne bouge.
 *
 * `boxes` : la liste qui contient la box (document ou masque), dans l'ordre
 * de rendu — à zIndex égal, c'est lui qui décide quelle box est devant.
 *
 * Un groupe n'est qu'un cadre transparent posé sur ses membres (cf.
 * groupFrameZIndex) : ce sont eux qui bougent, d'un bloc, en gardant leur
 * ordre entre eux.
 *
 * Reculer ne descend pas sous 0 (un zIndex négatif passerait sous le fond de
 * la colonne) : au plancher, ce sont les autres box qui montent.
 */
export const planBoxLayerMove = ({ boxes, boxId, step }) => {
  const box = boxes.find((b) => b.id === boxId);
  if (!box) return [];
  const layer = box.type === ItemTypes.GROUP_BOXES
    ? boxes.filter((b) => box.boxIds?.includes(b.id))
    : [box];
  if (layer.length === 0) return [];
  const others = boxes.filter((b) =>
    b.columnId === box.columnId &&
    b.type !== ItemTypes.GROUP_BOXES &&
    !layer.includes(b)
  );
  const zOf = (b) => ~~b.zIndex;
  // Rang d'empilement : le zIndex, puis l'ordre de rendu.
  const compareStack = (a, b) =>
    zOf(a) - zOf(b) || boxes.indexOf(a) - boxes.indexOf(b);
  const stacked = [...layer].sort(compareStack);
  const bottom = stacked[0];
  const top = stacked[stacked.length - 1];
  const changes = [];
  const shift = (list, delta) => list.forEach((b) => {
    changes.push({ id: b.id, zIndex: zOf(b) + delta });
  });

  if (step > 0) {
    const above = others.filter((b) => compareStack(b, bottom) > 0);
    if (above.length === 0) return changes;
    const next = above.sort(compareStack)[0];
    shift(layer, zOf(next) + 1 - zOf(bottom));
    return changes;
  }

  const below = others.filter((b) => compareStack(b, top) < 0);
  if (below.length === 0) return changes;
  const previous = below.sort(compareStack)[below.length - 1];
  const wanted = zOf(previous) - 1 - zOf(top);
  const delta = Math.max(wanted, -zOf(bottom));
  if (delta < 0) shift(layer, delta);
  if (delta > wanted) shift(others, delta - wanted);
  return changes;
};

export const mutateRemoveBox = (draft, id, maskId = null) => {
  const mask = draft.masks.find((m) => m.id === maskId);
  const boxesToUpdate = maskId === null ? draft.boxes : mask.boxes;
  const index = boxesToUpdate.findIndex((b) => b.id === id);
  const box = boxesToUpdate[index];
  boxesToUpdate.splice(index, 1);
  if (isATitleBox(box.type)) {
    mutateTitlesIndexes(draft, maskId);
    mutateResizeSummary(draft, maskId);
  }
  if (isAVariableBox(box.type)) {
    mutateTakeOutItem(draft, box.id);
  }
  mutateRemoveTriggersWithTriggerId({ triggerId: box.id, draft });
  if (draft.boxVariableSelectedId === id) {
    draft.boxVariableSelectedId = null;
  }
};

export const mutateSelectContainer = (draft, container) => {
  draft.selectedContainer = container;
  draft.selectedContainerIds = container?.id ? [container.id] : [];
  draft.activeContainerId = container?.id || null;
};
export const mutateSelectBox = (draft, box, group) => {
  // SI CTRL et une box est déjà sélectionnée
  if (group && draft.selectedBoxId) {
    // Si la box cliquée est dans le group de selection
    if (draft.groupSelection.includes(box.id)) {
      draft.groupSelection = draft.groupSelection.filter(
        (boxId) => boxId !== box.id
      );
      // SI box clickée est la box déjà sélectionnée
    } else if (draft.selectedBoxId === box.id) {
      // SI un groupe existe déjà
      if (draft.groupSelection.length > 0) {
        draft.selectedBoxId = draft.groupSelection[0];
        draft.groupSelection.shift();
      }
      // SI box clickée n'est pas la box déjà sélectionnée
    } else {
      // SI box clickée est dans la même page que la box selectionnée (maitre)
      //  TODO : Voir la gestion des groupes ?
      // Max une hauteur de page entre le haut de la première et la fin de la seconde ?
      // if (box.pageId === selectedBox.pageId) {
      draft.groupSelection.push(box.id);
      // }
    }
  } else {
    if (draft.selectedBoxId !== box.id) {
      draft.groupSelection = [];
      draft.selectedBoxId = box.id;
      draft.boxIsMovable = true;
    }
  }
};

export const initializeBoxContent = ({
  box,
  configuration,
  titles,
  mask,
  column,
  parentId,
}) => {
  /**
   * Add a new box
   */
  if (isATableBox(box.type)) {
    box.width = calculateWidth(configuration.margins, 0, 0);
  }

  if (box.type === ItemTypes.LINE) {
    if (box.copied === false) {
      box.content = {
        color: configuration.shapeColor.style,
        style: 'solid',
        height: 5,
        side: 'Bottom',
      };
      delete box.copied;
    }
  } else if (box.type === ItemTypes.SHAPE) {
    if (box.copied === false) {
      box.content = {
        color: configuration.shapeColor.style,
        shape: defaultItemValues.shape.content.shape,
      };
      delete box.copied;
    }
  } else if (
    box.type === ItemTypes.TEXT ||
    box.type === ItemTypes.TEXT_VARIABLE
  ) {
    if (box.copied === false) {
      box.content = {
        text: '',
        editorState: EditorState.createEmpty(),
      };
      delete box.copied;
      if (box.type === ItemTypes.TEXT_VARIABLE) {
        box.width = configuration.textStyles[box.textStyleId].width;
      }
    }
  } else if (box.type === ItemTypes.SELECTOR_VARIABLE) {
    if (box.copied === false) {
      box.width = configuration.selectorStyles[box.selectorStyleId].width;
      box.options = [
        {
          selected: false,
          label: EditorState.createWithContent(
            ContentState.createFromText('Oui'),
            decorator
          ),
          id: uuid(),
        },
        {
          selected: false,
          label: EditorState.createWithContent(
            ContentState.createFromText('Non'),
            decorator
          ),
          id: uuid(),
        },
      ];
    }
  } else if (
    box.type === ItemTypes.CHECKBOXS_VARIABLE ||
    box.type === ItemTypes.QUESTION
  ) {
    if (box.copied === false) {
      box.checkboxs = [
        {
          checked: false,
          label: EditorState.createWithContent(
            ContentState.createFromText('Oui'),
            decorator
          ),
          id: uuid(),
        },
        {
          checked: false,
          label: EditorState.createWithContent(
            ContentState.createFromText('Non'),
            decorator
          ),
          id: uuid(),
        },
      ];
    }
  } else if (box.type === ItemTypes.SUMMARY) {
    if (box.copied === false) {
      box.content = {
        configuration: {
          ...box.content.configuration,
          // Sans borne : les anciens index numériques (1 / titres - 1) ne
          // désignaient aucun titre et bloquaient le réglage « Du titre ».
          lineSummaryEnd: null,
          lineSummaryStart: null,
          indexTitle: box.content.indexTitle,
        },
      };
      delete box.copied;
    }
  } else if (box.type === ItemTypes.SPACE) {
    if (box.copied === false) {
      box.content = {
        sizeName: configuration.defaultSizeSpaceContainer,
      };
      delete box.copied;
    }
  } else if (box.type === ItemTypes.INFO_CARD) {
    if (box.copied === false) {
      // Démo au drop, comme le bloc web : l'utilisateur voit le rendu et
      // remplace par son texte.
      // Le premier modèle du template fait foi : une carte déposée doit
      // ressembler à ce que le configurateur montre, sac de style ET texte
      // de démonstration compris.
      const template = configuration.infoCardStyles?.[0]
        || defaultInfoCardConfiguration;
      box.content = {
        titleEditorState: EditorState.createWithContent(
          ContentState.createFromText(
            template.sample?.value || i18n.t('info_card_title_sample')
          ),
          decorator
        ),
        descriptionEditorState: EditorState.createWithContent(
          ContentState.createFromText(
            template.sample?.description || i18n.t('info_card_description_sample')
          ),
          decorator
        ),
        card: boxPropsFromStyle(template),
      };
      delete box.copied;
    }
  } else if (box.type === ItemTypes.ACCORDION_PANEL) {
    if (box.copied === false) {
      // Démo au drop, comme le bloc web : l'utilisateur voit le rendu et
      // remplace par son texte.
      box.content = {
        questionEditorState: EditorState.createWithContent(
          ContentState.createFromText(i18n.t('accordion_panel_question_sample')),
          decorator
        ),
        answerEditorState: EditorState.createWithContent(
          ContentState.createFromText(i18n.t('accordion_panel_answer_sample')),
          decorator
        ),
        // Le premier modèle du template fait foi, comme la Carte info.
        panel: boxPropsFromStyle(
          configuration.accordionPanelStyles?.[0] || defaultAccordionPanelConfiguration
        ),
      };
      delete box.copied;
    }
  }
  const newBox = {
    ...box,
    id: uuid(),
  };
  if (mask) {
    newBox.maskId = parentId;
    delete newBox.columnId;
  }
  if (column) {
    newBox.columnId = parentId;
    delete newBox.maskId;
  }
  return newBox;
};

const reninitializeTableWidth = ({ box, columnWidth }) => {
  const tableWidth = box.content.columns.reduce(
    (acc, col) => acc + col.width,
    0
  );
  const isReducingTable = tableWidth > columnWidth;

  let widthPerCol =
    Math.abs(tableWidth - columnWidth) / box.content.columns.length;
  let boxColsToChange = box.content.columns;
  let oldNumberOfColsToChange = null;

  while (
    boxColsToChange.length !== oldNumberOfColsToChange &&
    isReducingTable
  ) {
    oldNumberOfColsToChange = boxColsToChange.length;
    // eslint-disable-next-line no-loop-func
    boxColsToChange = boxColsToChange.filter(
      (c) => c.width - widthPerCol > MIN_COLUMN_WIDTH
    );
    widthPerCol = Math.abs(tableWidth - columnWidth) / boxColsToChange.length;
  }

  const columns = box.content.columns.map((c) => {
    if (boxColsToChange.some((col) => col.key === c.key))
      return {
        ...c,
        width: c.width - (isReducingTable ? widthPerCol : -widthPerCol),
      };
    return c;
  });

  return {
    ...box,
    content: {
      ...box?.content,
      columns,
    },
    width: columnWidth,
  };
};

export const reinitializeBoxWidthAndHeight = ({
  box,
  columnWidth,
  onResize = false,
  onCreate = false,
  header,
  footer,
}) => {
  if (
    onCreate === false &&
    isATableBox(box.type) &&
    box.content?.columns?.length > 0
  ) {
    return reninitializeTableWidth({ box, columnWidth });
  }

  let width =
    typeof box.width === 'number' && box.width > columnWidth
      ? columnWidth
      : box.width;

  let height =
    typeof box.height === 'number'
      ? (width / box.width) * box.height
      : box.height;

  // Image en contenir / remplir : hauteur en pixels, comme une box classique.
  const imageHeightFromRatio = isAImgBox(box.type) && !isFixedImageFit(box);

  const isHeightWithPixelTooBig =
    !imageHeightFromRatio &&
    typeof height === 'number' &&
    height > maxContainerHeight({ header, footer });

  const isHeightWithRatioTooBig =
    imageHeightFromRatio &&
    box.content?.ratio &&
    width / box.content?.ratio > maxContainerHeight({ header, footer });

  if (isHeightWithPixelTooBig) {
    if (typeof width === 'number') {
      width = (height * width) / maxContainerHeight({ header, footer });
      if (width > columnWidth) {
        width = columnWidth;
      }
    } else {
      width = columnWidth;
    }
    height = maxContainerHeight({ header, footer });
  }

  if (isHeightWithRatioTooBig) {
    if (typeof width === 'number') {
      width =
        (maxContainerHeight({ header, footer }) /
          (width / box.content?.ratio)) *
        width;
      height = maxContainerHeight({ header, footer });
    }
  }

  if (
    !onResize &&
    onCreate &&
    !box.copied &&
    (box.type === ItemTypes.TEXT ||
      box.type === ItemTypes.ACCORDION_PANEL ||
      box.type === ItemTypes.INFO_CARD ||
      box.type === ItemTypes.CHECKBOXS_VARIABLE ||
      box.type === ItemTypes.QUESTION ||
      box.type === ItemTypes.SELECTOR_VARIABLE ||
      box.type === ItemTypes.TEXT_VARIABLE)
  ) {
    width = columnWidth;
  }

  const nextBox = {
    ...box,
    width,
    height,
  };

  // Gabarit table + gridConfiguration souvent en « pleine page » alors que `width` est
  // déjà bornée à la colonne (multi-colonnes, marges). Sinon ∑ colonnes ≠ largeur boîte.
  if (
    onCreate &&
    !box.copied &&
    isATableBox(box.type) &&
    box.content?.columns?.length > 0 &&
    typeof width === 'number'
  ) {
    const w = width;
    const cols = box.content.columns;
    const n = cols.length;
    const base = Math.floor(w / n);
    nextBox.content = {
      ...box.content,
      columns: cols.map((col, i) => ({
        ...col,
        width: i === n - 1 ? w - base * (n - 1) : base,
      })),
    };
  }

  return nextBox;
};

// Box variable restée sans réponse : texte vide, rien de coché ni de choisi,
// section non choisie, image sur son jeton, cellule de tableau vide, PDF non
// importé (le nom du fichier est posé à l'import).
const isVariableBoxEmpty = (box) => {
  switch (box?.type) {
    case ItemTypes.CHECKBOXS_VARIABLE:
    case ItemTypes.QUESTION:
      return !box.checkboxs?.some((c) => c.checked);
    case ItemTypes.SELECTOR_VARIABLE:
      return !box.options?.some((o) => o.selected);
    case ItemTypes.TEXT_VARIABLE:
      return !box.content?.editorState?.getCurrentContent().getPlainText();
    case ItemTypes.SECTION_VARIABLE:
      return !box.answer;
    case ItemTypes.IMG_VARIABLE:
      return isImageValueMissing(box.content?.src);
    case ItemTypes.TABLE_VARIABLE:
      return isTableValueMissing(classicTableCells(box.content?.data));
    case ItemTypes.FILE_PDF_VARIABLE:
      return !box.fileName;
    default:
      return false;
  }
};

// Box variable optionnelle restée vide. Le champ texte garde son propre
// réglage (« Valeur requise ») ; les autres sont cochés `optional` dans le
// modèle. Les variables texte custom, elles, portent le flag sur la variable.
export const isOptionalBoxEmpty = (box) =>
  (box?.type === ItemTypes.TEXT_VARIABLE ? !box.value_required : !!box?.optional) &&
  isVariableBoxEmpty(box);

export const hasVariableBoxError = (box, variables) => {
  // Vide, une variable optionnelle n'attend pas de réponse ; dès qu'on y
  // répond, ses règles (nombre de réponses…) valent de nouveau.
  if (isOptionalBoxEmpty(box)) return { error: false, message: '' };
  if (
    box?.type === ItemTypes.CHECKBOXS_VARIABLE ||
    box.type === ItemTypes.QUESTION
  ) {
    if (box.checkboxs.filter((c) => c.checked).length < box.minNumberOfAnwser) {
      return {
        error: true,
        message: `${i18n.t('number-of-answer-to-fill')} ${
          box.minNumberOfAnwser - box.checkboxs.filter((c) => c.checked).length
        }`,
      };
    }
  }
  if (box?.type === ItemTypes.SELECTOR_VARIABLE) {
    if (box.options.filter((c) => c.selected).length < box.minNumberOfAnwser) {
      return {
        error: true,
        message: `${i18n.t('number-of-answer-to-fill')} ${
          box.minNumberOfAnwser - box.options.filter((c) => c.selected).length
        }`,
      };
    }
  }
  if (box?.type === ItemTypes.TEXT_VARIABLE) {
    if (isTextBoxValueMissing(box.content.editorState.getCurrentContent().getPlainText(), box.value_required)) {
      return {
        error: true,
        message: `${i18n.t('required-text-input')}`,
      };
    }
  }
  if (box?.type === ItemTypes.SECTION_VARIABLE) {
    if (!box.answer) {
      return {
        error: true,
        message: `${i18n.t('non-resolved-question')}`,
      };
    }
  }
  if (box?.type === ItemTypes.CUSTOM_TEXT_VARIABLE) {
    if (isTextValueMissing(variables ?? {}, box.id, box.value)) {
      return {
        error: true,
        message: `${i18n.t('missing-value')}`,
      };
    }
  }
  if (box?.type === ItemTypes.VARIABLE_GROUP) {
    if (
      box.variableIds?.some(
        (vId) =>
          variables[vId] && !variables[vId].notUsed && isTextValueMissing(variables, vId)
      )
    ) {
      return {
        error: true,
        message: `${i18n.t('missing-value')}`,
      };
    }
  }
  if (box?.type === ItemTypes.IMG_VARIABLE) {
    if (isImageValueMissing(box.content.src)) {
      return {
        error: true,
        message: `${i18n.t('missing-value')}`,
      };
    }
  }
  if (box?.type === ItemTypes.TABLE_VARIABLE) {
    if (isTableValueMissing(classicTableCells(box.content?.data))) {
      return {
        error: true,
        message: `${i18n.t('missing-value')}`,
      };
    }
  }
  if (box?.type === ItemTypes.FILE_PDF_VARIABLE) {
    if (!box.fileName) {
      return {
        error: true,
        message: `${i18n.t('missing-value')}`,
      };
    }
  }
  return { error: false, message: '' };
};

export const getVariableSelectionKey = (boxType) => {
  switch (boxType) {
    case ItemTypes.CHECKBOXS_VARIABLE:
    case ItemTypes.QUESTION:
      return 'checkboxs';
    case ItemTypes.SELECTOR_VARIABLE:
      return 'options';

    default:
      return null;
  }
};

export const simulateVariableTextBox = ([key, variable]) => {
  return {
    ...variable,
    type: ItemTypes.CUSTOM_TEXT_VARIABLE,
    positions: { horizontal: 'start', vertical: 'center' },
    zIndex: 0,
    width: '100%',
    content: null,
    height: 'auto',
    name: variable.name,
    value: variable.value,
    remark: variable.remark,
    id: key,
  };
};

export const simulateVariableGroupBox = (variableGroup) => {
  return {
    ...variableGroup,
    type: ItemTypes.VARIABLE_GROUP,
    positions: { horizontal: 'start', vertical: 'center' },
    zIndex: 0,
    width: '100%',
    content: null,
    height: 'auto',
  };
};

export const createBoxesFromPdfToImage = ({ data, draft }) => {
  const containerIndex = draft.containers.findIndex(
    (c) => c.id === draft.tempContainerPdfToImage.id
  );
  const container = draft.containers[containerIndex];
  container.KeyPdfToImageParent = data.KeyPdfToImage;
  const effectivePageWidth = draft.landscape ? pageHeight : pageWidth;

  if (!draft.tempContainerPdfToImage.drawMode) {
    mutateCreateMask({ draft, name: '', KeyPdfToImage: data.KeyPdfToImage });
    const mask = draft.masks[draft.masks.length - 1];
    const box = initializeBoxContent({
      box: {
        ...defaultItemValues.img,
        type: ItemTypes.IMG,
        height: 'auto',
        width: 695,
        content: {
          src: data.image_url,
        },
      },
      configuration: draft.configuration,
      titles: draft.titles,
      mask,
      parentId: mask.id,
    });

    box.width = effectivePageWidth - 1;
    box.top = 0;
    box.left = 0;
    mask.boxes = [box];
    const maskBox = initializeBoxContent({
      box: { ...defaultItemValues.mask, type: ItemTypes.MASK },
      configuration: draft.configuration,
      titles: draft.titles,
    });
    mutateAddContainers({
      draft,
      index: containerIndex,
      drawMode: true,
      box: maskBox,
      KeyPdfToImage: data.KeyPdfToImage,
    });
    mutateInjectMask({ draft, maskId: mask.id, boxId: maskBox.id });
  } else {
    let newBox = initializeBoxContent({
      box: {
        ...defaultItemValues.img,
        type: ItemTypes.IMG,
        height: 'auto',
        width: 695,
        content: {
          src: data.image_url,
        },
      },
      configuration: draft.configuration,
      titles: draft.titles,
    });
    newBox.width = effectivePageWidth - 149;
    newBox.top = 1;
    newBox.left = 73;

    newBox = reinitializeBoxWidthAndHeight({
      box: newBox,
      columnWidth: computeColumnWidth({
        landscape: draft.landscape,
        marginRight: 0,
        marginLeft: 0,
      }),
      onCreate: true,
      header: draft.header,
      footer: draft.footer,
    });
    mutateAddContainers({
      index: containerIndex,
      drawMode: true,
      draft,
      box: newBox,
      KeyPdfToImage: data.KeyPdfToImage,
      from: 'PdfToImageSizeMaxDefault',
    });
  }
};

export const newQuestionBox = ({
  gridConfiguration,
  landscape,
  configuration,
  header,
  footer,
}) => {
  let newBox = initializeBoxContent({
    box: {
      ...gridConfiguration[ItemTypes.CHECKBOXS_VARIABLE],
      type: ItemTypes.QUESTION,
      copied: false,
      onlyForm: true,
    },
  });
  newBox = reinitializeBoxWidthAndHeight({
    box: newBox,
    columnWidth: computeColumnWidth({
      landscape,
      marginRight: configuration.margins.right * gridUnit,
      marginLeft: configuration.margins.left * gridUnit,
    }),
    onCreate: true,
    header,
    footer,
  });
  return newBox;
};

export const mutateDefaultBoxValue = (boxes, isTemplate) =>
  isTemplate
    ? boxes
    : boxes.map((box) => {
        if (!isAVariableBox(box.type)) {
          return box;
        }
        if (isATextBox(box.type)) {
          return {
            ...box,
            defaultValue: { content: box.content, hideField: box.hideField },
          };
        }
        if (
          box.type === ItemTypes.CHECKBOXS_VARIABLE ||
          box.type === ItemTypes.QUESTION
        ) {
          return {
            ...box,
            defaultValue: {
              checkboxs: box.checkboxs,
              hideField: box.hideField,
            },
          };
        }
        if (box.type === ItemTypes.SELECTOR_VARIABLE) {
          return {
            ...box,
            defaultValue: { options: box.options, hideField: box.hideField },
          };
        }
        if (box.type === ItemTypes.SECTION_VARIABLE) {
          return {
            ...box,
            defaultValue: {
              sectionId: box.sectionId,
              hideField: box.hideField,
            },
          };
        }
        if (box.type === ItemTypes.FILE_PDF_VARIABLE) {
          return {
            ...box,
            defaultValue: {
              hideField: box.hideField,
            },
          };
        }
        if (box.type === ItemTypes.TABLE_VARIABLE) {
          return {
            ...box,
            defaultValue: {
              hideField: box.hideField,
            },
          };
        }
        return box;
      });

export const getLeftMostBoxes = (boxes) =>
  Math.min(...boxes.map((box) => box.left));

export const getTopMostBoxes = (boxes) =>
  Math.min(...boxes.map((box) => box.top));

export const getGroupBoxWidth = (boxesBoundingRect) => {
  const rightest = Math.max(...boxesBoundingRect.map(({ right }) => right));
  const leftest = Math.min(...boxesBoundingRect.map(({ left }) => left));

  return rightest - leftest;
};

export const getGroupBoxHeight = (boxesBoundingRect) => {
  const bottomest = Math.max(...boxesBoundingRect.map(({ bottom }) => bottom));
  const topest = Math.min(...boxesBoundingRect.map(({ top }) => top));

  return bottomest - topest;
};

export const getGroupBoxesDimensions = (allGroupBoxes, zoomFactor) => {
  const groupBoxes = allGroupBoxes.filter((b) => !b.hideField);
  const boxesBoundingRect = groupBoxes.map((box) =>
    getChildDimensionsAndPositions({ zoomFactor, child: box })
  );
  const groupBoxesPositions = groupBoxes.map((box, idx) => {
    const positions =
      (typeof box.originalRotation === 'number'
        ? box.originalRotation
        : ~~box.rotation) === 0
        ? { top: box.top, left: box.left }
        : findExtremePoints({
            child: {
              top: box.top,
              left: box.left,
              width: boxesBoundingRect[idx].width,
              height: boxesBoundingRect[idx].height,
            },
            rotation:
              typeof box.originalRotation === 'number'
                ? box.originalRotation
                : ~~box.rotation,
          });

    return positions;
  });

  const left = getLeftMostBoxes(groupBoxesPositions);
  const top = getTopMostBoxes(groupBoxesPositions);
  const width = getGroupBoxWidth(boxesBoundingRect) / zoomFactor;
  const height = getGroupBoxHeight(boxesBoundingRect) / zoomFactor;
  return { width, height, top, left };
};

export const initializeGroupBoxes = (groupBoxes, zoomFactor) => {
  const { width, height, left, top } = getGroupBoxesDimensions(
    groupBoxes,
    zoomFactor
  );

  return {
    ...defaultItemValues.group_boxes,
    id: uuid(),
    left,
    top,
    width,
    height,
    type: ItemTypes.GROUP_BOXES,
    columnId: groupBoxes[0].columnId,
    maskId: groupBoxes[0].maskId,
    boxIds: groupBoxes.map((b) => b.id),
  };
};

const findExtremePoints = ({ child, rotation }) => {
  const radians = (rotation * Math.PI) / 180;

  // Calculate the four corners of the box before rotation
  const corners = [
    { x: child.left, y: child.top }, // top-left
    { x: child.left + child.width, y: child.top }, // top-right
    { x: child.left + child.width, y: child.top + child.height }, // bottom-right
    { x: child.left, y: child.top + child.height }, // bottom-left
  ];

  // Calculate center point
  const centerX = child.left + child.width / 2;
  const centerY = child.top + child.height / 2;

  // Rotate each corner around the center
  const rotatedCorners = corners.map((corner) => {
    const dx = corner.x - centerX;
    const dy = corner.y - centerY;

    return {
      x: centerX + (dx * Math.cos(radians) - dy * Math.sin(radians)),
      y: centerY + (dx * Math.sin(radians) + dy * Math.cos(radians)),
    };
  });

  // Find the extreme points
  const left = Math.min(...rotatedCorners.map((corner) => corner.x));
  const top = Math.min(...rotatedCorners.map((corner) => corner.y));

  return { left, top };
};

export const getChildDimensionsAndPositions = ({ zoomFactor, child }) => {
  const originalEl = document.getElementById(`box-container-${child.id}`);
  const clone = originalEl.cloneNode(true);
  clone.id = 'temp-clone';
  clone.style.visibility = 'hidden';
  const currentTransform = clone.style.transform;

  if (currentTransform && currentTransform !== 'none') {
    const withoutRotate = currentTransform.replace(/\s*rotate\([^)]*\)/g, '');
    clone.style.transform = withoutRotate;
  }

  originalEl.parentNode.insertBefore(clone, originalEl.nextSibling);
  const cloneDimensionsRect = clone.getBoundingClientRect();
  if (currentTransform && currentTransform !== 'none') {
    const initalRotate = currentTransform.replace(
      /\s*rotate\([^)]*\)/g,
      ` rotate(${
        typeof child.originalRotation === 'number'
          ? child.originalRotation
          : ~~child.rotation
      }deg)`
    );
    clone.style.transform = initalRotate;
  }
  const clonePositionsRect = clone.getBoundingClientRect();
  clone.parentNode.removeChild(clone);
  return {
    width: cloneDimensionsRect.width / zoomFactor,
    height: cloneDimensionsRect.height / zoomFactor,
    right: clonePositionsRect.right,
    left: clonePositionsRect.left,
    top: clonePositionsRect.top,
    bottom: clonePositionsRect.bottom,
  };
};

export const shouldMeasureGroupBox = (boxes, box) => {
  if (typeof box.groupTop === 'undefined') return;
  const groupBox = boxes.find(
    (b) => b.type === ItemTypes.GROUP_BOXES && b.boxIds.includes(box.id)
  );
  if (!groupBox) return;
  groupBox.shouldMeasure = true;
};

/**
 * Recale le cadre d'un groupe sur ses membres après les avoir déplacés (aligner
 * ou répartir les box du groupe). Sans ça le cadre garderait son ancienne
 * emprise : poignée de déplacement plus large que le contenu, et un décalage du
 * groupe entier au prochain drag.
 * La géométrie mesurée (client*) est rafraîchie en asynchrone par le rendu (cf.
 * `alignToken` dans Box.jsx) : l'emprise est donc reconstruite ici à partir des
 * positions déjà écrites et des tailles mesurées, pour être juste dès la même
 * mutation.
 */
export const mutateFitGroupBoxToBoxes = (groupBox, boxesInGroup = []) => {
  const rects = boxesInGroup
    .filter((b) => !b.hideField)
    .map((b) => ({
      left: b.left,
      top: b.top,
      width: b.clientWidth ?? b.width ?? 0,
      height: b.clientHeight ?? b.height ?? 0,
    }));
  if (!rects.length) return;
  const left = Math.min(...rects.map((r) => r.left));
  const top = Math.min(...rects.map((r) => r.top));
  groupBox.left = left;
  groupBox.top = top;
  groupBox.width = Math.max(...rects.map((r) => r.left + r.width)) - left;
  groupBox.height = Math.max(...rects.map((r) => r.top + r.height)) - top;
  groupBox.alignToken = uuid();
  boxesInGroup.forEach((b) => {
    b.groupTop = top;
    // Référence de rotation du groupe : elle suit la nouvelle position, sinon
    // faire tourner le groupe ramènerait ses box là où elles étaient.
    b.originalLeft = b.left;
    b.originalTop = b.top;
  });
};

// Types dont le rendu est purement géométrique : les mettre à l'échelle dans
// les deux dimensions ne change que leur dessin. Le reste est piloté par une
// taille de police, qu'aucun facteur ne sait suivre : ces box gardent leur
// hauteur, qui vient de leur contenu.
const SCALABLE_GROUP_MEMBER_TYPES = [
  ItemTypes.SHAPE,
  ItemTypes.IMG,
  ItemTypes.IMG_VARIABLE,
];

// Planchers d'une mise à l'échelle de groupe. Un texte ne se replie pas en
// dessous de quelques mots par ligne ; une colonne de tableau garde la largeur
// minimale que lui laisse aussi son redimensionnement direct (MIN_COLUMN_WIDTH,
// cf. Table — pas importé ici, boxes.js ne dépend pas des composants).
const MIN_SCALED_TEXT_WIDTH = 40;
const MIN_SCALED_COLUMN_WIDTH = 20;

const scaleNumber = (value, scale) =>
  typeof value === 'number' ? value * scale : value;

/** Un membre de groupe dont les deux dimensions suivent l'échelle. */
export const isScalableGroupMember = (box) =>
  SCALABLE_GROUP_MEMBER_TYPES.includes(box?.type);

/**
 * Un tableau dont les colonnes suivent l'échelle : elles portent des largeurs
 * en px, que la box seule n'emmène pas. Pas s'il est tourné : Table recale sa
 * position à chaque changement de largeur (cf. effectiveTableWidth), ce qui
 * s'ajouterait au déplacement du groupe.
 */
export const scalesTableColumns = (box) =>
  isATableBox(box?.type) && !box.rotation && box.content?.columns?.length > 0;

/**
 * Un membre dont la largeur suit l'échelle. C'est le cas de tout ce qui se
 * recompose dans sa box — un texte rétréci avec son groupe doit se réécrire
 * plus étroit, sans quoi il déborde de l'emprise et le groupe se retrouve plus
 * grand que la taille demandée. Un tableau tourné se contente de se déplacer.
 */
export const scalesWidthWithGroup = (box) =>
  !isATableBox(box?.type) || scalesTableColumns(box);

/**
 * Plus petite échelle acceptable pour un groupe : celle où son texte le plus
 * étroit, ou sa colonne de tableau la plus étroite, touche son plancher. En
 * dessous, le texte se replierait mot à mot (et pousserait tout le reste) et
 * les colonnes deviendraient illisibles. Un plancher déjà franchi (texte déjà
 * plus étroit) ne bloque que la réduction, jamais l'agrandissement.
 */
export const groupMinScale = (boxesInGroup = []) => {
  const floors = boxesInGroup.flatMap((box) => {
    if (scalesTableColumns(box)) {
      return box.content.columns
        .filter((col) => typeof col.width === 'number' && col.width > 0)
        .map((col) => MIN_SCALED_COLUMN_WIDTH / col.width);
    }
    if (isScalableGroupMember(box) || !scalesWidthWithGroup(box)) return [];
    return typeof box.width === 'number' && box.width > 0
      ? [MIN_SCALED_TEXT_WIDTH / box.width]
      : [];
  });
  return Math.min(1, Math.max(0, ...floors));
};

/**
 * Point fixe d'une mise à l'échelle de groupe : le coin haut-gauche, la
 * poignée étant sur le bord droit. Le groupe grandit donc vers la droite et
 * vers le bas, comme une box qu'on redimensionne.
 */
export const groupScaleAnchor = (groupBox) => ({
  x: groupBox.left,
  y: groupBox.top,
});

/** Place d'un membre après mise à l'échelle autour de l'ancre du groupe. */
export const scaledMemberPosition = ({ anchor, left, top, scale }) => ({
  left: anchor.x + (left - anchor.x) * scale,
  top: anchor.y + (top - anchor.y) * scale,
});

/**
 * Met un groupe à l'échelle, autour de son coin haut-gauche (la poignée est
 * sur le bord droit : le groupe grandit vers la droite et vers le bas).
 *
 * L'échelle est unique (pas de largeur/hauteur séparées) : une image, un cercle
 * ou un carré ont un ratio verrouillé, et une échelle non uniforme les
 * déformerait ou sortirait du cadre qu'on vient de dessiner. Elle est bornée
 * par groupMinScale.
 *
 * Chaque membre garde sa place relative dans le groupe : son écart au coin du
 * cadre est multiplié par le même facteur. Sa largeur suit aussi, pour que le
 * contenu se recompose dans la nouvelle emprise (colonnes comprises pour un
 * tableau) ; sa hauteur seulement s'il s'agit d'une forme ou d'une image, celle
 * d'un texte ou d'un tableau venant de son contenu, dont la police ne change
 * pas. Le cadre est recalé sur ses membres une fois tout le monde placé.
 */
export const mutateScaleGroupBox = ({ groupBox, boxesInGroup = [], scale: requestedScale }) => {
  if (!groupBox || typeof requestedScale !== 'number' || !isFinite(requestedScale) || requestedScale <= 0) return;
  if (!boxesInGroup.length) return;
  const scale = Math.max(requestedScale, groupMinScale(boxesInGroup));
  if (scale === 1) return;
  const anchor = groupScaleAnchor(groupBox);

  boxesInGroup.forEach((box) => {
    const moved = scaledMemberPosition({ anchor, left: box.left, top: box.top, scale });
    box.left = moved.left;
    box.top = moved.top;

    if (scalesWidthWithGroup(box)) {
      box.width = scaleNumber(box.width, scale);
      // Les tailles mesurées servent au recalage du cadre juste après : sans
      // elles, il se recalerait sur l'emprise d'avant la mise à l'échelle.
      box.clientWidth = scaleNumber(box.clientWidth, scale);
    }

    // Même répartition des colonnes : un tableau paramétrable, qui la garde en
    // pourcentages, n'a donc rien à resynchroniser.
    if (scalesTableColumns(box)) {
      box.content.columns.forEach((col) => {
        col.width = scaleNumber(col.width, scale);
      });
    }

    if (isScalableGroupMember(box)) {
      box.height = scaleNumber(box.height, scale);
      box.clientHeight = scaleNumber(box.clientHeight, scale);
      // Sous-formes d'une forme composée : même traitement que leur box (cf.
      // mutateRescaleSubShapes, que shapeUtils applique lors d'un resize
      // direct — pas importé ici, boxes.js ne dépend pas des composants).
      if (Array.isArray(box.content?.shapes)) {
        box.content.shapes.forEach((sub) => {
          sub.x = scaleNumber(sub.x ?? 0, scale);
          sub.y = scaleNumber(sub.y ?? 0, scale);
          sub.width = scaleNumber(sub.width, scale);
          sub.height = scaleNumber(sub.height, scale);
        });
      }
    }
    // Une ligne n'a pas de cas à part : sa hauteur est l'épaisseur de son trait,
    // que la règle ci-dessus laisse justement tranquille.
  });

  mutateFitGroupBoxToBoxes(groupBox, boxesInGroup);
  // Hauteur d'un texte ou d'un tableau : celle de son contenu une fois replié
  // dans sa nouvelle largeur, que seul le rendu connaît (la clientHeight
  // ci-dessus est celle d'avant). Le cadre se remesure donc sur le DOM au
  // prochain rendu (cf. Box.jsx), sans quoi le texte déborderait par le bas.
  groupBox.shouldMeasure = true;
};

export const mutateAlignBoxBy = ({ side, box, targetValue, boxes }) => {
  const { propertyToAlign, value } = alignedBoxValues({
    side,
    box,
    targetValue,
  });
  const oldLeft = box.left;
  const oldTop = box.top;
  box[propertyToAlign] += value;
  if (box.type === ItemTypes.GROUP_BOXES) {
    mutateMoveBoxFromGroup({
      box: box,
      boxes,
      newLeft: box.left,
      newTop: box.top,
      oldLeft,
      oldTop,
    });
  }
  box.alignToken = uuid();
};
